import { useEffect, useState, useRef, ChangeEvent } from "react";
import { useVariant } from "../../../../hooks/useVariant/useVariant";
import Accordion from "@JohnLewisPartnership/jl-design-system/dist/components/accordion/Accordion";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import {
  PrimaryButton,
  UnderlinedButton,
} from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import OptionList from "@JohnLewisPartnership/jl-design-system/dist/components/option/OptionList";
import RadioButton from "@JohnLewisPartnership/jl-design-system/dist/components/radio-button/RadioButton";
import Input from "@JohnLewisPartnership/jl-design-system/dist/elements/input/Input";
import FormFieldErrorMessage from "@JohnLewisPartnership/jl-design-system/dist/components/form-field-error-message/FormFieldErrorMessage";
import { calculateMinRollerBlindPrice } from "../../util/calculateMinRollerBlindPrice";
import { HorizontalRule } from "../../../HorizontalRule/HorizontalRule";
import {
  M2MHelpSection,
  MadeToMeasureHelpModal,
} from "../MadeToMeasureHelpModal/MadeToMeasureHelpModal";
import { PriceError } from "../PriceError/PriceError";
import { MadeToMeasureReviewModal } from "../MadeToMeasureReviewModal/MadeToMeasureReviewModal";
import { getM2MPrice } from "../../util/getM2MPrice";
import { FeatureFlags, Product, RuntimeConfig } from "../../../../types/types";
import {
  M2M_ROLLER_BLINDS_MIN_WIDTH,
  M2M_ROLLER_BLINDS_MIN_HEIGHT,
} from "../../util/constants";
import { RollerBlindConfiguration } from "../../configurationTypes";
import {
  getConfigFromRollerBlindsCookie,
  setRollerBlindsCookieFromConfig,
} from "../../util/madeToMeasureCookieHelper";
import styles from "./RollerBlindsForm.module.scss";

type Props = {
  product: Product;
  runtimeConfig: RuntimeConfig;
  flags: FeatureFlags;
};

export const RollerBlindsForm = ({ product, runtimeConfig, flags }: Props) => {
  const { variant } = useVariant(product);
  const priceFrom = calculateMinRollerBlindPrice(variant.price?.display);

  const m2mRollerBlindsForm = useRef<any>(null);
  const widthInput = useRef<any>(null);
  const heightInput = useRef<any>(null);
  const installationHeightInput = useRef<any>(null);

  const configDefault: RollerBlindConfiguration = {
    recess: "",
    width: 0,
    height: 0,
    installationHeight: 0,
    rollDirection: "",
    chainSide: "",
    chainColour: "",
  };

  const [radioButtonValues, setRadioButtonValues] = useState({
    recess: { value: "" },
    chainSide: { value: "" },
    rollDirection: { value: "" },
  });
  const [totalPrice, setTotalPrice] = useState("");
  const [isWidthValid, setIsWidthValid] = useState(true);
  const [isHeightValid, setIsHeightValid] = useState(true);
  const [isInstallationHeightValid, setIsInstallationHeightValid] =
    useState(true);
  const [widthError, setWidthError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [installationHeightError, setInstallationHeightError] = useState("");
  const [installationHeightHint, setInstallationHeightHint] = useState("");
  const [isReviewButtonEnabled, setIsReviewButtonEnabled] = useState(false);
  const [isPriceCalculating, setIsPriceCalculating] = useState(false);
  const [priceError, setPriceError] = useState({ heading: "", body: "" });

  const [helpModal, updateHelpModal] = useState({
    open: false,
    visibleSection: { name: "", title: "" } as M2MHelpSection,
  });

  const helpSections: M2MHelpSection[] = [
    { name: "roller-blinds-window-fittings", title: "Window Fittings" },
    { name: "roller-blinds-measurements", title: "Measurements" },
    { name: "roller-blinds-roll-direction", title: "Roll Direction" },
  ];

  const toggleHelpModal = (section: M2MHelpSection = { name: "", title: "" }) =>
    updateHelpModal({ open: !helpModal.open, visibleSection: section });

  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  function onSubmit() {
    return;
  }

  function getConfigFromFormData(): RollerBlindConfiguration {
    if (!m2mRollerBlindsForm.current) return configDefault;

    const data: FormData = new FormData(m2mRollerBlindsForm.current);

    const rollerBlindConfig: RollerBlindConfiguration = {
      recess: data.get("recess")?.toString() || "",
      width: parseFloat(data.get("width")?.toString() || ""),
      height: parseFloat(data.get("height")?.toString() || ""),
      rollDirection: data.get("rollDirection")?.toString() || "",
      chainSide: data.get("chainSide")?.toString() || "",
      chainColour: "Chrome",
      installationHeight: parseFloat(
        data.get("installationHeight")?.toString() || "",
      ),
    };
    return rollerBlindConfig;
  }

  function setFormDataFromConfig(config: RollerBlindConfiguration | null) {
    if (config?.width) {
      widthInput.current.input.value = config.width;
    }
    if (config?.height) {
      heightInput.current.input.value = config.height;
    }
    if (config?.installationHeight) {
      installationHeightInput.current.input.value = config.installationHeight;
    }
    setRadioButtonValues({
      recess: { value: config?.recess ? config.recess : "Inside recess" },
      chainSide: { value: config?.chainSide ? config.chainSide : "Right" },
      rollDirection: {
        value: config?.rollDirection ? config.rollDirection : "Standard",
      },
    });
  }

  function onReviewModalClose(e: React.SyntheticEvent) {
    setIsReviewModalOpen(false);
    if (e) {
      e.preventDefault();

      const hrefTarget = e.currentTarget.getAttribute("href") || "";

      const scrollIntoView = () =>
        document
          .querySelector(hrefTarget)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });

      setTimeout(scrollIntoView, 0);
    }
  }

  function regEx(value: string) {
    const regExCondition = /^[0-9]+(\.([0-9]+)?)?$/;
    return regExCondition.test(value);
  }

  function regExDecimal(value: string) {
    const regExDecimalCondition = /^[0-9]+(\.([0-9]{1,2})?)?$/;
    return regExDecimalCondition.test(value);
  }

  function validateValue(input: any) {
    return input.value !== ""
      ? !input.validity?.badInput &&
          !input.validity?.valueMissing &&
          !input.validity?.typeMismatch &&
          !input.validity?.rangeUnderflow &&
          !input.validity?.rangeOverflow &&
          regExDecimal(input.value)
      : true;
  }

  function calcMinInstallationHeight() {
    const isDropValueValid = validateValue(heightInput.current.input);
    const isDropValueNull = heightInput.current.input.value === "";
    const dropValue =
      isDropValueValid && !isDropValueNull
        ? heightInput.current.input.value
        : 40;
    let minInstallationHeight = 0;
    if (dropValue <= 149) {
      minInstallationHeight = Math.round((dropValue * 0.6 + 60) * 100) / 100;
      if (minInstallationHeight > installationHeightInput.current.input.value) {
        installationHeightInput.current.input.value = "";
        setIsInstallationHeightValid(true);
        setInstallationHeightError("");
      }
    } else {
      minInstallationHeight = dropValue;
      if (minInstallationHeight > installationHeightInput.current.input.value) {
        installationHeightInput.current.input.value = "";
        setIsInstallationHeightValid(true);
        setInstallationHeightError("");
      }
    }
    setInstallationHeightHint(
      `Minimum installation height is ${minInstallationHeight}cm${isDropValueValid && !isDropValueNull ? `, for a drop of ${heightInput.current.input.value}cm` : ""}`,
    );
    installationHeightInput.current.input.min = minInstallationHeight;
  }

  const customMinMessage = (id: string, min: string, dependentInput: any) => {
    if (id === "installationHeight") {
      if (dependentInput) {
        const dependentInputMessage = dependentInput.value
          ? `required for drop of ${dependentInput.value} centimetres`
          : "";
        return `This is below the minimum installation height of ${min} centimetres ${dependentInputMessage}`;
      }
      return `This is below the minimum installation height of ${min} centimetres`;
    }
    return `This is below the minimum  ${id} of ${min} centimetres available for this fabric. Please try a different fabric`;
  };

  const createErrorMsgContent = (input: any, dependentInput?: any) => {
    if (input.value === "") {
      return `Check you’ve entered your measurements correctly in centimetres ${input.placeholder}`;
    } else if (!regEx(input.value)) {
      return `Check you’ve entered your measurements correctly in centimetres ${input.placeholder}`;
    } else if (!regExDecimal(input.value)) {
      return `Check your measurements are in centimetres and limit decimal numbers to two ${input.placeholder}0`;
    } else if (input.validity?.rangeUnderflow) {
      return customMinMessage(input.id, input.min, dependentInput);
    } else if (input.validity?.rangeOverflow) {
      return `Your measurement exceeds the maximum of ${input.max}cm available for this fabric`;
    } else {
      return `Check you’ve entered your measurements correctly in centimetres ${input.placeholder}`;
    }
  };

  function onRadioButtonChange(e: ChangeEvent) {
    const { name, value } = e.target as HTMLInputElement;

    const fields = {
      [name]: { value },
    };

    const updatedFields = { ...radioButtonValues, ...fields };

    setRadioButtonValues(updatedFields);
  }

  function validateWidth() {
    const widthValid = validateValue(widthInput.current.input);
    setIsWidthValid(widthValid);
    setWidthError(
      !widthValid ? createErrorMsgContent(widthInput.current.input) : "",
    );
    allInputsValid();
  }

  function validateHeight() {
    const heightValid = validateValue(heightInput.current.input);
    setIsHeightValid(heightValid);
    setHeightError(
      !heightValid ? createErrorMsgContent(heightInput.current.input) : "",
    );
    calcMinInstallationHeight();
    if (installationHeightInput.current.input.value !== "") {
      validateInstallationHeight();
    }
    allInputsValid();
  }

  function validateInstallationHeight() {
    const installationHeightValid = validateValue(
      installationHeightInput.current.input,
    );
    setIsInstallationHeightValid(installationHeightValid);
    setInstallationHeightError(
      !installationHeightValid
        ? createErrorMsgContent(
            installationHeightInput.current.input,
            heightInput.current.input,
          )
        : "",
    );
    allInputsValid();
  }

  async function getPrice() {
    setIsPriceCalculating(true);
    setPriceError({ heading: "", body: "" });

    const fabric = {
      variantId: variant.id,
      sku: variant.aliases.skuId,
      displayCode: variant.displayCode,
      productName: variant.title,
    };

    const configuration = getConfigFromFormData();

    const { price, error } = await getM2MPrice(
      runtimeConfig.aceApiBaseUrl,
      "MadeToMeasureRollerBlind",
      fabric,
      1,
      configuration,
    );

    setIsPriceCalculating(false);

    if (price) {
      setRollerBlindsCookieFromConfig(configuration);
      setTotalPrice(price.total);
      setIsReviewButtonEnabled(true);
    } else {
      setTotalPrice("");
      setIsReviewButtonEnabled(false);
    }
    if (error) {
      setPriceError(error);
    }
  }

  function allInputsValid() {
    if (
      widthInput.current.input.value !== "" &&
      heightInput.current.input.value !== "" &&
      installationHeightInput.current.input.value !== "" &&
      radioButtonValues.recess.value !== "" &&
      radioButtonValues.chainSide.value !== "" &&
      radioButtonValues.rollDirection.value !== "" &&
      validateValue(widthInput.current.input) &&
      validateValue(heightInput.current.input) &&
      validateValue(installationHeightInput.current.input)
    ) {
      //comparre value to existing formdata
      getPrice();
    } else {
      setIsReviewButtonEnabled(false);
      setTotalPrice("");
    }
  }

  useEffect(() => {
    calcMinInstallationHeight();
    setFormDataFromConfig(getConfigFromRollerBlindsCookie());
  }, []);

  useEffect(() => {
    allInputsValid();
  }, [radioButtonValues]);

  return (
    <div
      className={styles.RollerBlindsForm}
      data-testid="m2m-roller-blinds-form-container"
    >
      <h3
        className={styles.RollerBlindsForm__title}
        data-testid="m2m-roller-blinds-form-title"
      >
        Create your roller blind to get a price
      </h3>
      <div data-testid="m2m-roller-blinds-form-description">
        <p>
          Select your window fitting, add your measurements and roll direction.
        </p>
        <p>
          Your roller blind will be made and sent within 7 working days by
          expert Partners at Herbert Parkinson, our textile manufacturer in
          Lancashire.
        </p>
        <p>
          Blind cords and chains can present a safety risk for children and
          pets. We advise you to read and understand the information in the{" "}
          <UnderlinedButton
            tag="a"
            type="button"
            target="_blank"
            href={
              "/customer-services/shopping-with-us/product-recalls/product-safety-for-blinds-windows"
            }
          >
            child safety hub
          </UnderlinedButton>{" "}
          before placing your order.
        </p>
      </div>
      <form
        className={styles.RollerBlindsForm__form}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        id="m2mRollerBlindsForm"
        data-testid="m2m-roller-blinds-form"
        ref={m2mRollerBlindsForm}
      >
        <div
          className={styles.RollerBlindsForm__accordionSection}
          data-testid="m2m-roller-blinds-accordions-container"
        >
          <Accordion
            id="accordion-m2m-roller-blinds-window-fitting"
            data-testid="m2m-roller-blinds-accordion:window-fitting"
            label="1. Select a window fitting"
            animated={true}
            responsive
            open
          >
            <UnderlinedButton
              tag="a"
              type="button"
              onClick={() => toggleHelpModal(helpSections[0])}
              className={styles.RollerBlindsForm__help_link}
              data-testid="m2m-roller-blinds-help:window-fitting"
            >
              Learn about window fittings
            </UnderlinedButton>
            <div
              className={styles.OptionsList}
              data-testid="m2m-roller-blinds-recess-options"
            >
              <div className={styles.OptionsList__radio}>
                <img src="https://static.johnlewiscontent.com/v1/static/blind-inside-recess" />
                <RadioButton
                  id="inside-recess"
                  label="Inside recess"
                  value="Inside recess"
                  name="recess"
                  checked={radioButtonValues.recess.value === "Inside recess"}
                  onChange={onRadioButtonChange}
                />
              </div>
              <div className={styles.OptionsList__radio}>
                <img src="https://static.johnlewiscontent.com/v1/static/blind-outside-recess" />
                <RadioButton
                  id="outside-recess"
                  label="Outside recess"
                  value="Outside recess"
                  name="recess"
                  checked={radioButtonValues.recess.value === "Outside recess"}
                  onChange={onRadioButtonChange}
                />
              </div>
            </div>
            <div data-testid="m2m-roller-blinds-recess-description">
              <p>
                Please note: A blackout blind fitted INSIDE a recess will not
                block out light completely. The mechanism requires a 1cm gap to
                operate, so a &apos;frame&apos; of light will shine through.
                Also some blackout blinds have a Stab Stitch detail resulting in
                pinholes which cannot be prevented.
              </p>
              <p>
                If complete blackout is required, please consider installing a
                blind OUTSIDE the recess.
              </p>
            </div>
            <HorizontalRule />
            <div
              className={styles.RollerBlindsForm__chainSection}
              data-testid="m2m-roller-blinds-chain-section"
            >
              <div data-testid="m2m-roller-blinds-chain-description">
                <p>Choose your chain side</p>
                <p>Please note the colour of the chain will be chrome</p>
              </div>
              <div data-testid="m2m-roller-blinds-chain-options">
                <OptionList name="chainSide" isButtonList={false}>
                  <RadioButton
                    id="chain-right"
                    label="Right"
                    value="Right"
                    checked={radioButtonValues.chainSide.value === "Right"}
                    onChange={onRadioButtonChange}
                  />
                  <RadioButton
                    id="chain-left"
                    label="Left"
                    value="Left"
                    checked={radioButtonValues.chainSide.value === "Left"}
                    onChange={onRadioButtonChange}
                  />
                </OptionList>
              </div>
            </div>
          </Accordion>

          <Accordion
            id="accordion-m2m-roller-blinds-measurements"
            data-testid="m2m-roller-blinds-accordion:measurements"
            label="2. Enter your measurements"
            animated={true}
            responsive
            open={false}
          >
            <UnderlinedButton
              tag="a"
              type="button"
              onClick={() => toggleHelpModal(helpSections[1])}
              className={styles.RollerBlindsForm__help_link}
              data-testid="m2m-roller-blinds-help:measurements"
            >
              Please use our quick How To Measure guide for roller blinds to
              complete this section
            </UnderlinedButton>
            <div
              className={styles.RollerBlindsForm__inputContainer}
              data-testid="m2m-roller-blinds-input-container:width"
            >
              <label data-testid="m2m-roller-blinds-input-label:width">
                Total width (including brackets)
              </label>
              <Input
                ariaLabel="input-ariaLabel"
                id="width"
                name="width"
                type={"number"}
                ref={widthInput}
                placeholder="e.g. 150.5"
                required={true}
                readOnly={false}
                defaultValue={""}
                autoComplete={"off"}
                className={!isWidthValid ? styles["InputInvalid"] : undefined}
                onChange={validateWidth}
                extensionProps={{
                  min: M2M_ROLLER_BLINDS_MIN_WIDTH,
                }}
              />
              {!isWidthValid && (
                <div data-testid="m2m-roller-blinds-input-error:width">
                  <FormFieldErrorMessage message={widthError} />
                </div>
              )}
              <p
                className={styles.RollerBlindsForm__instruction}
                data-testid="m2m-roller-blinds-input-description:width"
              >
                N.B. The total width includes the roller blind AND the fixing
                brackets. The actual blind fabric will be 4.5cm narrower to
                accommodate the brackets. Minimum width is 41cm
              </p>
            </div>

            <div
              className={styles.RollerBlindsForm__inputContainer}
              data-testid="m2m-roller-blinds-input-container:height"
            >
              <label data-testid="m2m-roller-blinds-input-label:height">
                Drop
              </label>
              <Input
                ariaLabel="input-ariaLabel"
                id="height"
                name="height"
                type={"number"}
                ref={heightInput}
                placeholder="e.g. 165.2"
                required={true}
                readOnly={false}
                defaultValue={""}
                autoComplete={"off"}
                className={!isHeightValid ? styles["InputInvalid"] : undefined}
                onChange={validateHeight}
                extensionProps={{
                  min: M2M_ROLLER_BLINDS_MIN_HEIGHT,
                  "data-testid": "m2m-roller-blinds-input:height",
                }}
              />
              {!isHeightValid && (
                <div data-testid="m2m-roller-blinds-input-error:height">
                  <FormFieldErrorMessage message={heightError} />
                </div>
              )}
              <p
                className={styles.RollerBlindsForm__instruction}
                data-testid="m2m-roller-blinds-input-description:height"
              >
                Minimum drop is 40cm
              </p>
            </div>

            <div
              className={styles.RollerBlindsForm__inputContainer}
              data-testid="m2m-roller-blinds-input-container:installation-height"
            >
              <label data-testid="m2m-roller-blinds-input-label:installation-height">
                Installation height (to floor)
              </label>
              <Input
                ariaLabel="input-ariaLabel"
                id="installationHeight"
                name="installationHeight"
                type={"number"}
                ref={installationHeightInput}
                placeholder="e.g. 240.2"
                required={true}
                readOnly={false}
                defaultValue={""}
                autoComplete={"off"}
                className={
                  !isInstallationHeightValid
                    ? styles["InputInvalid"]
                    : undefined
                }
                onChange={validateInstallationHeight}
                extensionProps={{
                  "data-testid": "m2m-roller-blinds-input:installation-height",
                }}
              />
              {!isInstallationHeightValid && (
                <div data-testid="m2m-roller-blinds-input-error:installation-height">
                  <FormFieldErrorMessage message={installationHeightError} />
                </div>
              )}
              <div data-testid="m2m-roller-blinds-input-description:installation-height">
                <p
                  className={styles.RollerBlindsForm__instruction}
                  data-testid="m2m-roller-blinds-input-hint:installation-height"
                >
                  {installationHeightHint}
                </p>
                <p className={styles.RollerBlindsForm__instruction}>
                  N.B. This is measured vertically from the fixing point of the
                  blind down to the floor (even if installing above a kitchen
                  sink/worktop). It is required in order to comply with strict
                  child safety legislation.
                </p>
              </div>
            </div>
          </Accordion>

          <Accordion
            id="accordion-m2m-roller-blinds-roll-direction"
            data-testid="m2m-roller-blinds-accordion:roll-direction"
            label="3. Select a roll direction"
            animated={true}
            responsive
            open={false}
          >
            <UnderlinedButton
              tag="a"
              type="button"
              onClick={() => toggleHelpModal(helpSections[2])}
              className={styles.RollerBlindsForm__help_link}
              data-testid="m2m-roller-blinds-help:roll-direction"
            >
              What does roll direction mean?
            </UnderlinedButton>
            <div
              className={styles.OptionsList}
              data-testid="m2m-roller-blinds-roll-direction-options"
            >
              <div className={styles.OptionsList__radio}>
                <img src="https://static.johnlewiscontent.com/v1/static/blind-standard-roll" />
                <RadioButton
                  id="standard-roll"
                  label="Standard roll"
                  value="Standard"
                  name="rollDirection"
                  checked={radioButtonValues.rollDirection.value === "Standard"}
                  onChange={onRadioButtonChange}
                />
              </div>
              <div className={styles.OptionsList__radio}>
                <img src="https://static.johnlewiscontent.com/v1/static/blind-reverse-roll" />
                <RadioButton
                  id="reverse-roll"
                  label="Reverse roll"
                  value="Reverse"
                  name="rollDirection"
                  checked={radioButtonValues.rollDirection.value === "Reverse"}
                  onChange={onRadioButtonChange}
                />
              </div>
            </div>
          </Accordion>
        </div>
        <div
          className={styles.RollerBlindsForm__message}
          data-testid="m2m-roller-blinds-measurements-info-message"
        >
          <Message
            title="Please double check your measurements, as we custom make every blind."
            type="information"
          />
        </div>
        <div
          className={styles.RollerBlindsForm__summary}
          data-testid="m2m-roller-blinds-form-summary-container"
        >
          <div
            className={styles.RollerBlindsForm__summaryProduct}
            data-testid="m2m-roller-blinds-form-summary-product-container"
          >
            <p data-testid="m2m-roller-blinds-form-summary-product-title">
              {variant.title}
            </p>
            <p
              className={styles.RollerBlindsForm__pricePrefix}
              data-testid="m2m-roller-blinds-form-summary-product-price-label"
            >
              {totalPrice ? "Total roller blind price" : "Blind Price From"}
            </p>
            <p
              className={styles.RollerBlindsForm__priceText}
              data-testid="m2m-roller-blinds-form-summary-product-price"
            >
              {totalPrice || priceFrom}
            </p>
          </div>
          <div
            className={styles.RollerBlindsForm__summaryCta}
            data-testid="m2m-roller-blinds-form-summary-cta-container"
          >
            <PrimaryButton
              tag="button"
              type="button"
              disabled={!isReviewButtonEnabled}
              submitting={isPriceCalculating}
              onClick={() => setIsReviewModalOpen(!isReviewModalOpen)}
              data-testid="m2m-roller-blinds-form-summary-cta"
            >
              Review your roller blind
            </PrimaryButton>
          </div>
        </div>
        {priceError.heading && (
          <PriceError
            errorHeading={priceError.heading}
            errorBody={priceError.body}
          />
        )}
      </form>

      <MadeToMeasureHelpModal
        sections={helpSections}
        visibleSection={helpModal.visibleSection}
        isOpen={helpModal.open}
        onClose={toggleHelpModal}
      />

      {isReviewModalOpen && (
        <MadeToMeasureReviewModal
          configuration={getConfigFromFormData()}
          totalPrice={totalPrice}
          product={product}
          runtimeConfig={runtimeConfig}
          flags={flags}
          isOpen={isReviewModalOpen}
          onClose={onReviewModalClose}
          configurationReqType={"MadeToMeasureRollerBlind"}
        />
      )}
    </div>
  );
};
